import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const Pagination = ({ page, totalPages, total, pageSize, onPageChange, onPageSizeChange }) => {
  if (!totalPages || totalPages <= 1) {
    if (onPageSizeChange && total > 0) {
      return (
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100" data-testid="pagination">
          <p className="text-[10px] text-gray-500">Showing {total} of {total}</p>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-gray-400">Rows</span>
            <select
              data-testid="page-size-select"
              value={pageSize}
              onChange={e => onPageSizeChange(Number(e.target.value))}
              className="text-[10px] border border-gray-200 rounded px-1.5 py-0.5 text-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-200"
            >
              {PAGE_SIZE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      );
    }
    return null;
  }

  const pages = [];
  const maxVisible = 5;
  let start = Math.max(1, page - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
  for (let i = start; i <= end; i++) pages.push(i);

  return (
    <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100" data-testid="pagination">
      <p className="text-[10px] text-gray-500">
        Showing {Math.min((page - 1) * pageSize + 1, total)}–{Math.min(page * pageSize, total)} of {total}
      </p>
      <div className="flex items-center gap-2">
        {onPageSizeChange && (
          <div className="flex items-center gap-1.5 mr-2">
            <span className="text-[10px] text-gray-400">Rows</span>
            <select
              data-testid="page-size-select"
              value={pageSize}
              onChange={e => onPageSizeChange(Number(e.target.value))}
              className="text-[10px] border border-gray-200 rounded px-1.5 py-0.5 text-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-200"
            >
              {PAGE_SIZE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        )}
        <div className="flex items-center gap-1">
          <button
            data-testid="pagination-prev"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className="p-1 rounded-md hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-3.5 h-3.5 text-gray-600" />
          </button>
          {start > 1 && (
            <>
              <button onClick={() => onPageChange(1)} className="px-2 py-0.5 text-[10px] font-medium text-gray-600 rounded hover:bg-gray-100">1</button>
              {start > 2 && <span className="text-[10px] text-gray-400">...</span>}
            </>
          )}
          {pages.map(p => (
            <button
              key={p}
              data-testid={`pagination-page-${p}`}
              onClick={() => onPageChange(p)}
              className={`px-2 py-0.5 text-[10px] font-medium rounded transition-all ${p === page ? 'text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              style={p === page ? { backgroundColor: '#24549c' } : {}}
            >
              {p}
            </button>
          ))}
          {end < totalPages && (
            <>
              {end < totalPages - 1 && <span className="text-[10px] text-gray-400">...</span>}
              <button onClick={() => onPageChange(totalPages)} className="px-2 py-0.5 text-[10px] font-medium text-gray-600 rounded hover:bg-gray-100">{totalPages}</button>
            </>
          )}
          <button
            data-testid="pagination-next"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            className="p-1 rounded-md hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-3.5 h-3.5 text-gray-600" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
